import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import SFTypography from "../typography";
import { SFStack } from "../grid";
import {
  SFInfoIcon,
  SFPlaceHolder,
  SFTipIcon,
  SFWarningAmberIcon,
} from "../icons";

const VARIANT = {
  BLACK: "black",
  BLUE: "blue",
  RED: "red",
  WHITE: "white",
};

const ICON = {
  HELP: "help",
  INFO: "info",
  TIP: "tip",
  WARNING: "warning",
};

const WIDTH = {
  AUTO: "auto",
  NARROW: "narrow",
  WIDE: "wide",
};

const SFTooltipContent = ({ icon, heading, content }) => {
  return (
    <SFStack direction="row" spacing={1.25}>
      {icon}
      <SFStack direction="column" spacing={1}>
        {heading && typeof heading === "string" ? (
          <SFTypography variant="cardSectionHeading">{heading}</SFTypography>
        ) : (
          heading
        )}
        {content && typeof content === "string" ? (
          <SFTypography variant="tooltipText">{content}</SFTypography>
        ) : (
          content
        )}
      </SFStack>
    </SFStack>
  );
};

const SFTooltip = forwardRef(
  (
    {
      arrow = true,
      children,
      className,
      content,
      disableHoverListener,
      heading,
      icon,
      leaveDelay = 0,
      onClose,
      onOpen,
      open,
      placement = "bottom",
      variant = VARIANT.WHITE,
      width = WIDTH.AUTO,
      ...props
    },
    ref
  ) => {
    const tooltipWidth = getWidth(width);
    const tooltipIcon = getIcon(icon);
    return (
      <Tooltip
        {...props}
        ref={ref}
        arrow={arrow}
        componentsProps={{
          tooltip: {
            sx: {
              width: tooltipWidth,
              maxWidth: tooltipWidth === "auto" ? "none" : tooltipWidth,
            },
            className,
          },
        }}
        disableHoverListener={disableHoverListener}
        leaveDelay={leaveDelay}
        onClose={onClose}
        onOpen={onOpen}
        open={open}
        placement={placement}
        title={
          !!content && (
            <SFTooltipContent
              icon={tooltipIcon}
              heading={heading}
              content={content}
            />
          )
        }
        variant={variant}
      >
        {children}
      </Tooltip>
    );
  }
);

export default SFTooltip;

SFTooltip.propTypes = {
  /**
   * If `true`, adds an arrow to the tooltip.
   */
  arrow: PropTypes.bool,
  /**
   * Tooltip reference element. This needs to be able to hold a ref.
   */
  children: PropTypes.element.isRequired,
  /**
   * Override or extend the styles applied to the tooltip component using styled-components.
   */
  className: PropTypes.string,
  /**
   * The content of the tooltip. If it is a string, it will be rendered using the `tooltipText` variant.
   */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Do not respond to hover events.
   */
  disableHoverListener: PropTypes.bool,
  /**
   * The heading of the tooltip. Rendered above the tooltip content. If it is a string, it will be rendered using the `cardSectionHeading` variant.
   */
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Determines the icon to be displayed in the tooltip.
   */
  icon: PropTypes.oneOf(Object.values(ICON)),
  /**
   * The number of milliseconds to wait before hiding the tooltip.
   */
  leaveDelay: PropTypes.number,
  /**
   * Callback fired when the component requests to be closed.
   *
   * **Signature:**
   *
   * ```
   * function(event: React.SyntheticEvent) => void
   * ```
   * - `event`: The event source of the callback.
   */
  onClose: PropTypes.func,
  /**
   * Callback fired when the component requests to be opened.
   *
   * **Signature:**
   *
   * ```
   * function(event: React.SyntheticEvent) => void
   * ```
   * - `event`: The event source of the callback
   */
  onOpen: PropTypes.func,
  /**
   * If `true`, the component is shown.
   */
  open: PropTypes.bool,
  /**
   * Tooltip placement.
   */
  placement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top",
    "bottom",
  ]),
  /**
   * Determines the background color and border color of the tooltip.
   */
  variant: PropTypes.oneOf(Object.values(VARIANT)),
  /**
   * Determines the width of the tooltip.
   */
  width: PropTypes.oneOf(Object.values(WIDTH)),
};

const getWidth = (width) => {
  switch (width) {
  case WIDTH.NARROW:
    return "170px";
  case WIDTH.WIDE:
    return "265px";
  case WIDTH.AUTO:
  default:
    return "auto";
  }
};

const getIcon = (icon) => {
  switch (icon) {
  case ICON.INFO:
    return <SFInfoIcon color="grey400" />;
  case ICON.HELP:
    return <SFPlaceHolder color="grey400" />;
  case ICON.TIP:
    return <SFTipIcon color="grey400" />;
  case ICON.WARNING:
    return <SFWarningAmberIcon color="orange400" rounded />;
  default:
    return null;
  }
};
